/**
 * Scoring Adapter Registry
 *
 * Maps question types to their corresponding scoring adapter functions.
 * Similar to preview adapter registry but for scoring components.
 */

import { teamScoringAdapter } from './adapters/teamScoringAdapter';
import { playerScoringAdapter } from './adapters/playerScoringAdapter';
import { propScoringAdapter } from './adapters/propScoringAdapter';
import { overUnderScoringAdapter } from './adapters/overUnderScoringAdapter';
import { tiebreakerScoringAdapter } from './adapters/tiebreakerScoringAdapter';
import { customScoringAdapter } from './adapters/customScoringAdapter';
import { leaderScoringAdapter } from './adapters/leaderScoringAdapter';
import type { ScoringQuestionData } from './types';
import type { RenderableQuestionType } from '../questionRenderTypes';

/**
 * Registry of all scoring adapters
 *
 * Maps question type strings to their adapter functions.
 * Add new question types here as they're implemented.
 */
export const SCORING_ADAPTERS = {
  team: teamScoringAdapter,
  player: playerScoringAdapter,
  prop: propScoringAdapter,
  over_under: overUnderScoringAdapter,
  team_over_under: overUnderScoringAdapter,
  player_over_under: overUnderScoringAdapter,
  tiebreaker: tiebreakerScoringAdapter,
  custom: customScoringAdapter,
  player_leader: leaderScoringAdapter,
  team_leader: leaderScoringAdapter,
  // Main markets (DKPLAT-500): Moneyline/Spread are option-picks (score like a
  // team selection — mark the winning option); Total is a match-level Over/Under.
  moneyline: teamScoringAdapter,
  spread: teamScoringAdapter,
  total: overUnderScoringAdapter,
  // `satisfies` makes this map exhaustive over RenderableQuestionType at compile
  // time: a type added to that union but not here fails to build (and vice versa).
} satisfies Record<RenderableQuestionType, (data: ScoringQuestionData) => unknown>;

/**
 * Supported question types for scoring — the single source of truth
 * (see utils/questionRenderTypes.ts).
 */
export type ScoringQuestionType = RenderableQuestionType;

/**
 * Check if a question type is supported for scoring
 *
 * @param type - Question type string to check
 * @returns True if the type has a scoring adapter registered
 */
export function isValidScoringQuestionType(type: string): type is ScoringQuestionType {
  return type in SCORING_ADAPTERS;
}

/**
 * Get scoring adapter for a question type
 *
 * @param type - Question type string
 * @returns Adapter function or undefined if not found
 */
export function getScoringAdapter(type: string) {
  return isValidScoringQuestionType(type) ? SCORING_ADAPTERS[type] : undefined;
}
